import { useCallback, useEffect, useRef } from "react";
import Axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { useAppStore } from "../store";
import { notification } from "antd";
import { NotificationType } from "../utils";

type Props = {
  children: React.ReactNode;
};

export const base = "https://api-v2.medpharma.care";
export const stage = "https://api-v2-staging.medpharma.care";
export const tip = "http://localhost:9090";

const instance = Axios.create({
  baseURL: stage,
  headers: { "Content-Type": "application/json" },
});

/** Raw axios for refresh-token to avoid attaching expired token */
async function callRefreshToken(refreshToken: string) {
  const { data } = await Axios.post<{ token: string; refreshToken: string }>(
    `${base}/auth/refresh-token`,
    { refreshToken },
    { headers: { "Content-Type": "application/json" } },
  );
  return data;
}

const AxiosInterceptor = ({ children }: Props) => {
  const [api, contextHolder] = notification.useNotification();
  const { logOut, rehydrated, setToken } = useAppStore();
  const refreshingRef = useRef<Promise<unknown> | null>(null);

  const openNotificationWithIcon = useCallback(
    (type: NotificationType, title: string, message: string) => {
      api[type]({
        message: title,
        description: message,
      });
    },
    [api],
  );

  useEffect(() => {
    instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const { token: storeToken } = useAppStore.getState();
        const accessToken = storeToken?.token;
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        if (
          error.response?.status !== 401 ||
          !rehydrated ||
          originalRequest._retry
        ) {
          if (
            (error.response?.status === 401 ||
              error.response?.status === 403) &&
            rehydrated
          ) {
            openNotificationWithIcon(
              "warning",
              "Warning",
              "Session expired! Kindly sign in.",
            );
            logOut();
          }
          return Promise.reject(error);
        }

        const isRefreshRequest = originalRequest.url?.includes(
          "/auth/refresh-token",
        );
        if (isRefreshRequest) {
          logOut();
          return Promise.reject(error);
        }

        const refreshTokenValue = useAppStore.getState().token?.refreshToken;
        if (!refreshTokenValue) {
          logOut();
          return Promise.reject(error);
        }

        if (!refreshingRef.current) {
          refreshingRef.current = callRefreshToken(refreshTokenValue)
            .then((data) => {
              setToken({ token: data.token, refreshToken: data.refreshToken });
            })
            .catch(() => {
              openNotificationWithIcon(
                "warning",
                "Warning",
                "Session expired! Kindly sign in.",
              );
              logOut();
            })
            .finally(() => {
              refreshingRef.current = null;
            });
        }

        try {
          await refreshingRef.current;
          const newToken = useAppStore.getState().token?.token;
          if (newToken) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
        } catch {
          // logOut already called in refresh catch
        }
        return Promise.reject(error);
      },
    );

    instance.defaults.headers.get["Content-Type"] = "application/json";
    instance.defaults.headers.post["Content-Type"] = "application/json";
    instance.defaults.headers.patch["Content-Type"] = "application/json";
  }, [logOut, setToken, rehydrated, openNotificationWithIcon]);

  return (
    <>
      {children}
      {contextHolder}
    </>
  );
};

export default instance;
export { AxiosInterceptor };
